'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

export default function CodingHeroSection() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <section className="relative w-full min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-purple-900 via-indigo-900 to-blue-900">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      {/* Floating Code Icons */}
      <div className="absolute inset-0 pointer-events-none">
        <Icon name="CodeBracketIcon" size={60} className="absolute top-20 left-10 text-primary/20 animate-pulse delay-100" />
        <Icon name="CommandLineIcon" size={50} className="absolute top-40 right-20 text-primary/20 animate-pulse delay-300" />
        <Icon name="CpuChipIcon" size={70} className="absolute bottom-32 left-20 text-primary/20 animate-pulse delay-500" />
        <Icon name="BoltIcon" size={55} className="absolute bottom-20 right-32 text-primary/20 animate-pulse delay-700" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-20">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className={`${isVisible ? 'animate-fadeInUp delay-200' : 'opacity-0'}`}>
            <div className="inline-flex items-center gap-2 bg-primary/20 backdrop-blur-md border border-primary/30 rounded-full px-4 py-2 mb-6">
              <Icon name="SparklesIcon" size={16} className="text-primary" />
              <span className="text-xs font-semibold uppercase tracking-wider text-white">
                100% Job Oriented
              </span>
            </div>

            <h2 className="font-display text-5xl md:text-7xl uppercase tracking-tighter leading-none text-white mb-6">
              Code Your
              <br />
              <span className="text-primary">Dreams</span> Into
              <br />
              Reality
            </h2>

            <p className="text-lg text-white/80 mb-6 leading-relaxed">
              Break free from boring lectures. Dive into <span className="text-primary font-semibold">creative coding</span> that builds real projects, solves real problems, and lands real jobs.
            </p>

            <div className="space-y-4 mb-8">
              <div className="flex items-start gap-3">
                <Icon name="CheckCircleIcon" size={24} className="text-primary flex-shrink-0 mt-1" />
                <p className="text-white/90">
                  <span className="font-semibold text-white">Master languages that matter:</span> C, C++, Java, Python & more
                </p>
              </div>
              <div className="flex items-start gap-3">
                <Icon name="CheckCircleIcon" size={24} className="text-primary flex-shrink-0 mt-1" />
                <p className="text-white/90">
                  <span className="font-semibold text-white">Build, not memorize:</span> Create apps, games, and tools from day one
                </p>
              </div>
              <div className="flex items-start gap-3">
                <Icon name="CheckCircleIcon" size={24} className="text-primary flex-shrink-0 mt-1" />
                <p className="text-white/90">
                  <span className="font-semibold text-white">Industry-ready skills:</span> 100% job-oriented curriculum designed by experts
                </p>
              </div>
            </div>

            <Link
              href="/courses"
              className="group inline-flex items-center gap-2 bg-primary text-primary-foreground px-8 py-4 rounded-full text-base font-semibold uppercase tracking-wider hover:bg-primary/90 transition-all shadow-xl hover:shadow-2xl hover:scale-105">
              
              Start Coding Journey
              <Icon name="ArrowRightIcon" size={20} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          {/* Right Image */}
          <div className={`relative ${isVisible ? 'animate-fadeInScale delay-400' : 'opacity-0'}`}>
            <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-primary/30">
              <AppImage
                src="https://img.rocket.new/generatedImages/rocket_gen_img_1dfbc27cd-1766500363073.png"
                alt="Developer coding on multiple monitors with colorful code syntax highlighting, representing creative programming and software development"
                width={800}
                height={600}
                className="w-full h-auto" />
              
              <div className="absolute inset-0 bg-gradient-to-t from-purple-900/80 to-transparent"></div>
              <div className="absolute bottom-6 left-6 right-6">
                <p className="text-white font-display text-2xl uppercase tracking-tight">
                  "Don't just learn code.<br />Create the future."
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

}